import React, { createContext, useContext, useState } from 'react';
import { Checklist } from '#/types/message';
import { fetchChecklist } from '#/api/images';
import logger from '#/utils/logger';

export enum ChecklistState {
  OPEN = 'open',
  HIDDEN = 'hidden',
  CLOSED = 'closed'
}

interface ChecklistContextType {
  checklistId: string;
  setChecklistId: (id: string) => void;
  checklistState: ChecklistState;
  setChecklistState: (state: ChecklistState) => void;
  renderChecklist: boolean;

  getChecklist: (checklistId: string) => Promise<Checklist>;
  getCachedChecklist: (checklistId: string) => Checklist | undefined;
  isLoadingChecklist: boolean;
  checklistError: string | null;
}

const ChecklistContext = createContext<ChecklistContextType | undefined>(undefined);

const ChecklistCache = new Map<string, Checklist>();

export function ChecklistProvider({ children }: { children: React.ReactNode }) {
  const [checklistId, setChecklistId] = useState<string>('');
  const [checklistState, setChecklistState] = useState<ChecklistState>(ChecklistState.CLOSED);
  const [isLoadingChecklist, setIsLoadingChecklist] = useState(false);
  const [checklistError, setChecklistError] = useState<string | null>(null);

  const renderChecklist = (checklistState === ChecklistState.HIDDEN || checklistState === ChecklistState.OPEN);

  const getCachedChecklist = (checklistId: string): Checklist | undefined => {
    return ChecklistCache.get(checklistId);
  };

  const getChecklist = async (checklistId: string): Promise<Checklist> => {
    if (ChecklistCache.has(checklistId)) {
      return ChecklistCache.get(checklistId)!;
    }

    setIsLoadingChecklist(true);
    setChecklistError(null);

    try {
      const data = await fetchChecklist(checklistId);
      ChecklistCache.set(checklistId, data);
      return data;
    } catch (error) {
      logger.error('Error loading checklist:', error);
      const errorMessage = 'Failed to load checklist';
      setChecklistError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoadingChecklist(false);
    }
  };

  return (
    <ChecklistContext.Provider value={{
      checklistId,
      setChecklistId,
      checklistState,
      setChecklistState,
      renderChecklist,
      getChecklist,
      getCachedChecklist,
      isLoadingChecklist,
      checklistError,
    }}>
      {children}
    </ChecklistContext.Provider>
  );
}

export function useChecklist() {
  const context = useContext(ChecklistContext);
  if (context === undefined) {
    throw new Error('useChecklist must be used within a ChecklistProvider');
  }
  return context;
}
